import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Truck, Store, ShoppingBasket, Loader2, Leaf, MapPin, Ruler } from 'lucide-react';
import { toast } from 'sonner';
import { storeApi } from '../../lib/api';
import { useCart, formatAUD } from './CartContext';
import { useDeliveryQuote } from './useDeliveryQuote';
import { AreaCalculatorDialog } from './area/AreaCalculatorDialog';

function DeliveryQuoteLine({ isDelivery, quote, checking, hasInput }) {
  if (!isDelivery) return <p className="flex justify-between text-white/80"><span>Farm pickup</span><span data-testid="summary-delivery">Free</span></p>;
  if (!hasInput) return <p className="flex justify-between text-white/80"><span>Delivery</span><span className="text-white/50 text-xs" data-testid="summary-delivery">Enter suburb for a quote</span></p>;
  if (checking) return <p className="flex justify-between text-white/80"><span>Delivery</span><span className="flex items-center gap-1 text-xs" data-testid="summary-delivery"><Loader2 className="h-3 w-3 animate-spin" /> Checking…</span></p>;
  if (quote?.matched) {
    return (
      <div>
        <p className="flex justify-between text-white/80"><span>Delivery · {quote.zone_name}</span><span data-testid="summary-delivery">{quote.free_delivery ? 'Free' : formatAUD(quote.fee)}</span></p>
        {quote.free_over_m2 != null && !quote.free_delivery && <p className="text-[11px] text-white/50 mt-0.5">Free delivery on orders of {quote.free_over_m2} m² or more</p>}
      </div>
    );
  }
  return <p className="flex justify-between text-white/80"><span>Delivery</span><span className="text-xs text-orange-200" data-testid="summary-delivery">Quoted by phone</span></p>;
}

function CartItems({ varieties }) {
  const { items, updateQuantity, removeItem, addItem } = useCart();
  const [adding, setAdding] = useState('');
  const [measuring, setMeasuring] = useState(null);
  const available = varieties.filter((v) => v.in_stock && !items.some((i) => i.variety_id === v.id));

  return (
    <div className="bg-white rounded-2xl p-6" style={{ border: '1px solid var(--s-sand)' }} data-testid="cart-items">
      <h2 className="font-heading font-bold text-lg mb-5 flex items-center gap-2"><ShoppingBasket className="h-5 w-5" style={{ color: 'var(--s-green)' }} /> Your turf</h2>

      {items.length === 0 ? (
        <div className="text-center py-10 rounded-xl" style={{ background: 'var(--s-cream)' }} data-testid="cart-empty">
          <Leaf className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--s-green-3)' }} />
          <p className="text-sm" style={{ color: 'var(--s-muted)' }}>No turf added yet. Pick a variety below or <Link to="/" className="underline font-medium" style={{ color: 'var(--s-green)' }}>browse the range</Link>.</p>
        </div>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'var(--s-sand)' }}>
          {items.map((item) => (
            <li key={item.variety_id} className="py-4 flex flex-wrap items-center gap-4" data-testid={`cart-item-${item.slug}`}>
              <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                {item.photo ? <img src={storeApi.photoUrl(item.photo)} alt="" className="w-full h-full object-cover" /> : <div className="store-placeholder w-full h-full" />}
              </div>
              <div className="flex-1 min-w-[140px]">
                <p className="font-semibold">{item.name}</p>
                <p className="text-xs" style={{ color: 'var(--s-muted)' }}>{formatAUD(item.price_per_m2)}/m² · min {item.min_order_m2} m²</p>
              </div>
              <div className="flex flex-col items-start gap-1">
                <label className="flex items-center gap-2 text-sm">
                  <input type="number" min={item.min_order_m2 || 1} step="1" value={item.quantity_m2} onChange={(e) => updateQuantity(item.variety_id, e.target.value === '' ? '' : Number(e.target.value))} className="store-input w-24 py-2" data-testid={`qty-input-${item.slug}`} />
                  <span style={{ color: 'var(--s-muted)' }}>m²</span>
                </label>
                <button type="button" onClick={() => setMeasuring(item.variety_id)} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: 'var(--s-green)' }} data-testid={`measure-${item.slug}`}><Ruler className="h-3 w-3" /> Work out my area</button>
              </div>
              <p className="w-24 text-right font-heading font-bold" data-testid={`line-total-${item.slug}`}>{formatAUD((Number(item.quantity_m2) || 0) * item.price_per_m2)}</p>
              <button onClick={() => removeItem(item.variety_id)} className="p-2 rounded-full hover:bg-red-50 text-red-500" aria-label="Remove" data-testid={`remove-item-${item.slug}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}

      <AreaCalculatorDialog open={measuring !== null} onOpenChange={(o) => { if (!o) setMeasuring(null); }} onUse={(m2) => { if (measuring) updateQuantity(measuring, m2); }} />

      {available.length > 0 && (
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <select value={adding} onChange={(e) => setAdding(e.target.value)} className="store-input flex-1" data-testid="add-variety-select">
            <option value="">Add another variety…</option>
            {available.map((v) => <option key={v.id} value={v.id}>{v.name} — {formatAUD(v.price_per_m2)}/m²</option>)}
          </select>
          <button type="button" disabled={!adding} onClick={() => { const v = varieties.find((x) => x.id === adding); if (v) { addItem(v, v.min_order_m2 || 10); setAdding(''); } }} className="store-btn store-btn-dark py-2.5 disabled:opacity-50" data-testid="add-variety-btn"><Plus className="h-4 w-4" /> Add</button>
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM = { customer_name: '', email: '', phone: '', delivery_method: 'delivery', address: '', suburb: '', postcode: '', preferred_date: '', notes: '' };

export default function OrderPage() {
  const navigate = useNavigate();
  const { items, totalM2, subtotal, clear } = useCart();
  const [varieties, setVarieties] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { storeApi.getVarieties().then((r) => setVarieties(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isDelivery = form.delivery_method === 'delivery';
  const { quote, checking, hasInput } = useDeliveryQuote({ suburb: form.suburb, postcode: form.postcode, totalM2, enabled: isDelivery });
  const deliveryFee = !isDelivery ? 0 : quote?.matched ? quote.fee : null;
  const total = subtotal + (deliveryFee || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error('Add at least one turf variety to your order'); return; }
    const bad = items.find((i) => !(Number(i.quantity_m2) > 0) || Number(i.quantity_m2) < (i.min_order_m2 || 0));
    if (bad) { toast.error(`${bad.name}: minimum order is ${bad.min_order_m2} m²`); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, items: items.map((i) => ({ variety_id: i.variety_id, quantity_m2: Number(i.quantity_m2) })) };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      const { data } = await storeApi.createOrder(payload);
      clear();
      navigate(`/shop/track?order=${data.order_number}&email=${encodeURIComponent(data.email)}`, { state: { justPlaced: true } });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not place order. Please check your details.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-16" data-testid="order-page">
      <p className="store-eyebrow mb-3">Order turf</p>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">Tell us what you need</h1>
      <p className="max-w-xl mb-10" style={{ color: 'var(--s-muted)' }}>No payment is taken online. We will call to confirm your order, delivery cost and the best day to lay.</p>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        <div className="space-y-6">
          <CartItems varieties={varieties} />

          <div className="bg-white rounded-2xl p-6 space-y-5" style={{ border: '1px solid var(--s-sand)' }} data-testid="customer-details">
            <h2 className="font-heading font-bold text-lg">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm"><span className="block font-medium mb-1.5">Full name *</span><input required value={form.customer_name} onChange={set('customer_name')} className="store-input" data-testid="order-name-input" /></label>
              <label className="text-sm"><span className="block font-medium mb-1.5">Phone *</span><input required type="tel" value={form.phone} onChange={set('phone')} className="store-input" data-testid="order-phone-input" /></label>
              <label className="text-sm sm:col-span-2"><span className="block font-medium mb-1.5">Email *</span><input required type="email" value={form.email} onChange={set('email')} className="store-input" data-testid="order-email-input" /></label>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Delivery or pickup?</p>
              <div className="grid grid-cols-2 gap-3">
                {[{ v: 'delivery', label: 'Deliver to me', icon: Truck }, { v: 'pickup', label: 'Pick up from farm', icon: Store }].map((o) => (
                  <button key={o.v} type="button" onClick={() => setForm((f) => ({ ...f, delivery_method: o.v }))} className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold border-2 transition-colors ${form.delivery_method === o.v ? 'border-[var(--s-green)] bg-[var(--s-green)] text-white' : 'border-[var(--s-sand)] hover:border-[var(--s-green-3)]'}`} data-testid={`method-${o.v}`}>
                    <o.icon className="h-4 w-4" /> {o.label}
                  </button>
                ))}
              </div>
            </div>

            {isDelivery && (
              <div className="grid sm:grid-cols-3 gap-4" data-testid="delivery-fields">
                <label className="text-sm sm:col-span-3"><span className="block font-medium mb-1.5">Street address *</span><input required={isDelivery} value={form.address} onChange={set('address')} className="store-input" data-testid="order-address-input" /></label>
                <label className="text-sm sm:col-span-2"><span className="block font-medium mb-1.5">Suburb *</span><input required={isDelivery} value={form.suburb} onChange={set('suburb')} className="store-input" data-testid="order-suburb-input" /></label>
                <label className="text-sm"><span className="block font-medium mb-1.5">Postcode</span><input value={form.postcode} onChange={set('postcode')} className="store-input" data-testid="order-postcode-input" /></label>
                {hasInput && !checking && (
                  <p className={`sm:col-span-3 flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${quote?.matched ? 'bg-green-50 text-green-800' : 'bg-orange-50 text-orange-800'}`} data-testid="delivery-quote-msg">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                    {quote?.matched
                      ? <span>Delivery to <strong>{form.suburb || form.postcode}</strong> ({quote.zone_name}): <strong>{quote.free_delivery ? 'free' : formatAUD(quote.fee)}</strong>{quote.free_over_m2 != null && !quote.free_delivery ? ` — free on ${quote.free_over_m2} m² or more` : ''}.</span>
                      : <span>{quote?.message || 'We will confirm a delivery quote by phone.'}</span>}
                  </p>
                )}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm"><span className="block font-medium mb-1.5">Preferred date</span><input type="date" value={form.preferred_date} onChange={set('preferred_date')} className="store-input" data-testid="order-date-input" /></label>
            </div>
            <label className="text-sm block"><span className="block font-medium mb-1.5">Notes</span><textarea rows={3} value={form.notes} onChange={set('notes')} placeholder="Access instructions, lay day, questions…" className="store-input" data-testid="order-notes-input" /></label>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 rounded-2xl p-6 text-white" style={{ background: 'var(--s-green)' }} data-testid="order-summary">
          <h2 className="font-heading font-bold text-lg mb-5">Order summary</h2>
          <ul className="space-y-2 text-sm mb-5">
            {items.map((i) => (
              <li key={i.variety_id} className="flex justify-between gap-3 text-white/80"><span>{i.name} × {i.quantity_m2 || 0} m²</span><span>{formatAUD((Number(i.quantity_m2) || 0) * i.price_per_m2)}</span></li>
            ))}
          </ul>
          <div className="border-t border-white/15 pt-4 space-y-2 text-sm">
            <p className="flex justify-between text-white/80"><span>Total area</span><span data-testid="summary-total-m2">{totalM2.toFixed(0)} m²</span></p>
            <p className="flex justify-between text-white/80"><span>Turf</span><span data-testid="summary-subtotal">{formatAUD(subtotal)}</span></p>
            <DeliveryQuoteLine isDelivery={isDelivery} quote={quote} checking={checking} hasInput={hasInput} />
            <p className="flex justify-between text-xl font-heading font-bold pt-2 border-t border-white/15"><span>{deliveryFee == null ? 'Turf total' : 'Total'}</span><span data-testid="summary-total">{formatAUD(total)}</span></p>
            <p className="text-xs text-white/60">{deliveryFee == null ? 'Delivery will be quoted when we confirm your order.' : 'Prices include GST. No payment taken online.'}</p>
          </div>
          <button type="submit" disabled={submitting || items.length === 0} className="store-btn store-btn-primary w-full mt-6" data-testid="place-order-btn">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : 'Place order request'}
          </button>
        </aside>
      </form>
    </div>
  );
}
